/**
 * PATH: packages/ui/src/calendar/composeFromEmail.ts
 * PURPOSE: Canonical contract for the "Schedule meeting from email"
 *          intent — the email reader fires it, `CalendarApp` consumes it.
 *
 * WHY this lives in `@quotech/ui/calendar`:
 *   - The calendar OWNS this contract (it's the receiver). The email
 *     reader is one of N possible producers (today: email-hub
 *     EmailContextMenu; tomorrow: AI Hub "schedule" suggestion;
 *     EventComposer's own "duplicate" affordance; etc.). The receiver
 *     should declare the shape so producers stay honest.
 *   - Splitting the const across packages is a latent bug — a literal
 *     drift between sender and receiver would silently break the
 *     handoff (event never reaches the listener). Re-export from the
 *     single place that already lives inside the calendar bundle.
 *
 * Handshake:
 *   Producer (e.g. email reader):
 *     1. `sessionStorage.setItem(PENDING_COMPOSE_STORAGE_KEY, JSON.stringify(payload))`
 *     2. `window.dispatchEvent(new CustomEvent(COMPOSE_FROM_EMAIL_EVENT, { detail: payload }))`
 *     3. Navigate to `/calendar` (so `CalendarApp` mounts if it isn't already)
 *
 *   Consumer (`CalendarApp`):
 *     1. On mount, check `sessionStorage` for a pending payload, drain it.
 *        This handles the cross-route case where the event fired BEFORE
 *        the listener was wired (operator was on /email when they
 *        triggered the action).
 *     2. While mounted, listen for the window event for hot fires (operator
 *        is already on /calendar in another tab affordance — rare but
 *        cheap to support).
 *     3. Convert the payload into `CreateEventInput` defaults and open the
 *        EventComposer.
 *
 * Rationale for sessionStorage (not localStorage):
 *   - The intent is transient — once the operator opens the composer (or
 *     navigates away) the payload is stale. localStorage would survive
 *     across tabs/restarts and cause the composer to re-open on the next
 *     visit. sessionStorage matches the lifetime of the intent.
 *
 * Rationale for window event in addition to sessionStorage:
 *   - sessionStorage doesn't fire change notifications within the same tab.
 *     Without the window event, if `CalendarApp` is already mounted, it
 *     wouldn't notice the storage write and would only reflect the intent
 *     if the operator manually re-navigated.
 *
 * Backend contract:
 *   The `source_email` UUID is the master Django Email PK (NOT the
 *   Outlook message id). On submit, `api.createEvent({source_email})`
 *   stamps `Event.source_email_id` (a nullable FK that already exists
 *   on the calendar_app.Event model). The link is currently used to
 *   prevent the inviting email from re-classifying as `meeting_request`
 *   on the next AI pass (closed-loop signal — see calendar_app PR1).
 */

/** Window event name. Use `window.dispatchEvent(new CustomEvent(...))`.
 *  Detail is `ComposeFromEmailPayload`. */
export const COMPOSE_FROM_EMAIL_EVENT = 'quotech.calendar.compose-from-email'

/** sessionStorage key — value is a JSON-stringified `ComposeFromEmailPayload`. */
export const PENDING_COMPOSE_STORAGE_KEY = 'quotech.calendar.pending-compose'

export interface ComposeFromEmailAttendee {
  email: string
  display_name?: string
}

export interface ComposeFromEmailPayload {
  /** Master Django Email PK (UUID). Stamped onto Event.source_email_id
   *  on create. */
  source_email: string
  /** Pre-filled meeting title. We use the email subject as-is — operators
   *  consistently prefer "RE: <subject>" or just "<subject>" rather than
   *  "Meeting:" prefixes (subjective; revisit if Newman et al complain). */
  title: string
  /** Pre-filled description. Producer should pass a plain-text excerpt;
   *  the composer's description field is currently plain-text. */
  description: string
  /** Attendees harvested from the email's To/Cc lists. The composer's
   *  AttendeePicker accepts `{email, display_name}` and stamps role:
   *  'REQUIRED' on insert. The current operator is NOT included
   *  (they're the organiser; calendar_app adds them automatically). */
  attendees: ComposeFromEmailAttendee[]
}

/** Build the next-15-min slot + 30min duration default time window.
 *  Same heuristic as `EventComposer.buildInitial()`. Exposed here so
 *  producers can stamp deterministic times into the payload, or callers
 *  can call this directly when they want to pre-fill from a different
 *  context. */
export function defaultMeetingTimeWindow(now: Date = new Date()): {
  start_at: string
  end_at: string
} {
  const start = new Date(Math.ceil(now.getTime() / 900_000) * 900_000)
  const end = new Date(start.getTime() + 30 * 60_000)
  return {
    start_at: start.toISOString(),
    end_at: end.toISOString(),
  }
}

/** Producer-side helper: stash the payload AND fire the event AND (if
 *  navigate is supplied) navigate to /calendar. Pass `navigate` from
 *  react-router-dom's `useNavigate()` — we don't import it here to keep
 *  this file router-agnostic. */
export function triggerComposeFromEmail(
  payload: ComposeFromEmailPayload,
  navigate?: (path: string) => void,
): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      PENDING_COMPOSE_STORAGE_KEY,
      JSON.stringify(payload),
    )
  } catch {
    /* ignore — private browsing / quota / etc. The window event below
     * is still useful when CalendarApp is mounted. */
  }
  try {
    window.dispatchEvent(
      new CustomEvent(COMPOSE_FROM_EMAIL_EVENT, { detail: payload }),
    )
  } catch {
    /* old browsers without CustomEvent ctor — ignore. */
  }
  if (navigate) navigate('/calendar')
}

/** Consumer-side helper: drain the sessionStorage payload (returns the
 *  payload + clears the key in a single hop). CalendarApp calls this
 *  on mount to handle the cross-route case. */
export function drainPendingComposeFromEmail(): ComposeFromEmailPayload | null {
  if (typeof window === 'undefined') return null
  let raw: string | null = null
  try {
    raw = window.sessionStorage.getItem(PENDING_COMPOSE_STORAGE_KEY)
    if (raw) window.sessionStorage.removeItem(PENDING_COMPOSE_STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as ComposeFromEmailPayload
    if (
      !parsed ||
      typeof parsed.source_email !== 'string' ||
      typeof parsed.title !== 'string' ||
      typeof parsed.description !== 'string' ||
      !Array.isArray(parsed.attendees)
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}
